import {
  childSessionKeysToWatch,
  transcriptToLogLines,
  type FleetSessionLogLine,
} from "../fleet/fleet-plan-session-logs.ts";
import type { GatewayBrowserClient } from "../gateway.ts";
import type {
  BibleFinalSummary,
  FleetDecisionItem,
  FleetWorkPlanRecord,
  FleetWorkPlanSummary,
} from "../types.ts";

function isUnknownWorkPlanMethodError(err: unknown): boolean {
  return String(err).includes("unknown method: fleet.workPlan");
}

export function formatFleetWorkPlanError(err: unknown): string {
  if (isUnknownWorkPlanMethodError(err)) {
    return "Work plan needs a newer gateway. Run pnpm build, restart the gateway, and refresh.";
  }
  return String(err);
}

export type FleetWorkPlanState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  agentsSelectedId: string | null;
  workPlansLoading: boolean;
  workPlansError: string | null;
  workPlans: FleetWorkPlanSummary[];
  workPlanYoutrackConfigured: boolean;
  workPlanSelectedId: string | null;
  workPlanDetail: FleetWorkPlanRecord | null;
  workPlanDetailLoading: boolean;
  workPlanBusy: boolean;
  workPlanOpenDecisions: FleetDecisionItem[];
  workPlanBibleSummary: BibleFinalSummary | null;
  /** Expanded agent-card role ids, preserved across detail refreshes. */
  workPlanExpandedRoleIds: Set<string>;
  /** Live-refresh interval handle for the selected plan. */
  workPlanPollInterval: number | null;
  /** Git branches available for the target-branch autocomplete. */
  workPlanBranches: string[];
  workPlanBranchesLoading: boolean;
  workPlanBranchesError: string | null;
  /** Child-session transcript lines for expanded agent cards. */
  workPlanSessionLogs: Record<string, FleetSessionLogLine[]>;
};

export async function loadFleetWorkPlans(state: FleetWorkPlanState) {
  if (!state.client || !state.connected || !state.agentsSelectedId) {
    state.workPlans = [];
    state.workPlansError = null;
    return;
  }
  state.workPlansLoading = true;
  state.workPlansError = null;
  try {
    const res = await state.client.request<{
      agentId: string;
      plans: FleetWorkPlanSummary[];
      youtrackConfigured: boolean;
    }>("fleet.workPlan.list", { agentId: state.agentsSelectedId });
    state.workPlans = Array.isArray(res?.plans) ? res.plans : [];
    state.workPlanYoutrackConfigured = Boolean(res?.youtrackConfigured);
  } catch (err) {
    state.workPlansError = formatFleetWorkPlanError(err);
    state.workPlans = [];
  } finally {
    state.workPlansLoading = false;
  }
}

export async function refreshFleetWorkPlans(state: FleetWorkPlanState) {
  await loadFleetWorkPlans(state);
  if (state.workPlanSelectedId) {
    await loadFleetWorkPlanDetail(state, state.workPlanSelectedId);
  }
}

export async function loadFleetWorkPlanBranches(state: FleetWorkPlanState) {
  if (!state.client || !state.connected || !state.agentsSelectedId) {
    state.workPlanBranches = [];
    state.workPlanBranchesError = null;
    return;
  }
  state.workPlanBranchesLoading = true;
  state.workPlanBranchesError = null;
  try {
    const res = await state.client.request<{
      agentId: string;
      branches: string[];
    }>("fleet.workPlan.listBranches", { agentId: state.agentsSelectedId });
    state.workPlanBranches = Array.isArray(res?.branches) ? res.branches : [];
  } catch (err) {
    state.workPlanBranchesError = formatFleetWorkPlanError(err);
    state.workPlanBranches = [];
  } finally {
    state.workPlanBranchesLoading = false;
  }
}

export async function loadFleetWorkPlanDetail(
  state: FleetWorkPlanState,
  planId: string,
  options: { quiet?: boolean } = {},
) {
  if (!state.client || !state.connected || !state.agentsSelectedId) {
    return;
  }
  if (!options.quiet) {
    state.workPlanDetailLoading = true;
  }
  state.workPlanSelectedId = planId;
  try {
    const res = await state.client.request<{
      plan: FleetWorkPlanRecord;
      openDecisions?: FleetDecisionItem[];
      bibleSummary?: BibleFinalSummary;
    }>("fleet.workPlan.get", {
      agentId: state.agentsSelectedId,
      planId,
    });
    // Ignore stale responses: the user may have selected a different plan
    // while this request was in flight.
    if (state.workPlanSelectedId !== planId) {
      return;
    }
    state.workPlansError = null;
    state.workPlanDetail = res?.plan ?? null;
    state.workPlanOpenDecisions = Array.isArray(res?.openDecisions) ? res.openDecisions : [];
    state.workPlanBibleSummary = res?.bibleSummary ?? null;
  } catch (err) {
    // Quiet poll ticks must not clobber the dashboard with a transient RPC error.
    if (!options.quiet) {
      state.workPlansError = formatFleetWorkPlanError(err);
    }
    if (!options.quiet && state.workPlanSelectedId === planId) {
      state.workPlanDetail = null;
    }
  } finally {
    if (!options.quiet) {
      state.workPlanDetailLoading = false;
    }
  }
}

export async function createFleetWorkPlan(
  state: FleetWorkPlanState,
  params: {
    goal: string;
    plannerAgentId: string;
    youtrackProject?: string;
    parentSessionKey?: string;
    targetBranch?: string;
  },
): Promise<FleetWorkPlanRecord | null> {
  if (!state.client || !state.connected || !state.agentsSelectedId) {
    return null;
  }
  const goal = params.goal.trim();
  if (!goal) {
    return null;
  }
  state.workPlanBusy = true;
  state.workPlansError = null;
  try {
    const res = await state.client.request<{ plan: FleetWorkPlanRecord }>("fleet.workPlan.create", {
      agentId: state.agentsSelectedId,
      plannerAgentId: params.plannerAgentId,
      goal,
      youtrackProject: params.youtrackProject?.trim() || undefined,
      parentSessionKey: params.parentSessionKey?.trim() || undefined,
      targetBranch: params.targetBranch?.trim() || undefined,
    });
    const plan = res?.plan ?? null;
    if (plan) {
      state.workPlanDetail = plan;
      state.workPlanSelectedId = plan.id;
      await loadFleetWorkPlans(state);
    }
    return plan;
  } catch (err) {
    state.workPlansError = formatFleetWorkPlanError(err);
    return null;
  } finally {
    state.workPlanBusy = false;
  }
}

export async function stopFleetWorkPlan(state: FleetWorkPlanState, planId: string) {
  if (!state.client || !state.connected || !state.agentsSelectedId) {
    return;
  }
  state.workPlanBusy = true;
  state.workPlansError = null;
  try {
    const res = await state.client.request<{
      plan: FleetWorkPlanRecord;
      stoppedSubagents?: number;
    }>("fleet.workPlan.stop", {
      agentId: state.agentsSelectedId,
      planId,
    });
    state.workPlanDetail = res?.plan ?? state.workPlanDetail;
    await loadFleetWorkPlans(state);
  } catch (err) {
    state.workPlansError = formatFleetWorkPlanError(err);
  } finally {
    state.workPlanBusy = false;
  }
}

export async function resumeFleetWorkPlan(state: FleetWorkPlanState, planId: string) {
  if (!state.client || !state.connected || !state.agentsSelectedId) {
    return;
  }
  state.workPlanBusy = true;
  state.workPlansError = null;
  try {
    const res = await state.client.request<{ plan: FleetWorkPlanRecord }>("fleet.workPlan.resume", {
      agentId: state.agentsSelectedId,
      planId,
    });
    state.workPlanDetail = res?.plan ?? state.workPlanDetail;
    await loadFleetWorkPlans(state);
  } catch (err) {
    state.workPlansError = formatFleetWorkPlanError(err);
  } finally {
    state.workPlanBusy = false;
  }
}

export async function approveFleetWorkPlan(state: FleetWorkPlanState, planId: string) {
  if (!state.client || !state.connected || !state.agentsSelectedId) {
    return;
  }
  state.workPlanBusy = true;
  state.workPlansError = null;
  try {
    const res = await state.client.request<{ plan: FleetWorkPlanRecord }>(
      "fleet.workPlan.approve",
      {
        agentId: state.agentsSelectedId,
        planId,
      },
    );
    state.workPlanDetail = res?.plan ?? state.workPlanDetail;
    await loadFleetWorkPlans(state);
  } catch (err) {
    state.workPlansError = formatFleetWorkPlanError(err);
  } finally {
    state.workPlanBusy = false;
  }
}

export async function publishFleetWorkPlanYouTrack(
  state: FleetWorkPlanState,
  planId: string,
  project: string,
) {
  if (!state.client || !state.connected || !state.agentsSelectedId) {
    return;
  }
  state.workPlanBusy = true;
  state.workPlansError = null;
  try {
    const res = await state.client.request<{ plan: FleetWorkPlanRecord }>(
      "fleet.workPlan.publishYouTrack",
      {
        agentId: state.agentsSelectedId,
        planId,
        project: project.trim(),
      },
    );
    state.workPlanDetail = res?.plan ?? state.workPlanDetail;
    await loadFleetWorkPlans(state);
  } catch (err) {
    state.workPlansError = formatFleetWorkPlanError(err);
  } finally {
    state.workPlanBusy = false;
  }
}

export async function spawnFleetWorkPlanUnit(
  state: FleetWorkPlanState,
  planId: string,
  unitId: string,
  sessionKey: string,
  workerAgentId?: string,
) {
  if (!state.client || !state.connected || !state.agentsSelectedId) {
    return;
  }
  state.workPlanBusy = true;
  state.workPlansError = null;
  try {
    const res = await state.client.request<{
      plan: FleetWorkPlanRecord | null;
      unitId: string;
      childSessionKey: string;
      runId: string;
      warning?: string;
    }>("fleet.workPlan.spawnUnit", {
      agentId: state.agentsSelectedId,
      planId,
      unitId,
      sessionKey: sessionKey.trim(),
      workerAgentId: workerAgentId?.trim() || undefined,
    });
    state.workPlanDetail = res?.plan ?? state.workPlanDetail;
    await loadFleetWorkPlans(state);
  } catch (err) {
    state.workPlansError = formatFleetWorkPlanError(err);
  } finally {
    state.workPlanBusy = false;
  }
}

export async function resolveFleetWorkPlanDecision(
  state: FleetWorkPlanState,
  params: { planId: string; decisionId: string; resolution: "continue" | "stop" },
) {
  if (!state.client || !state.connected || !state.agentsSelectedId) {
    return;
  }
  state.workPlanBusy = true;
  state.workPlansError = null;
  try {
    const res = await state.client.request<{
      plan: FleetWorkPlanRecord;
      openDecisions?: FleetDecisionItem[];
      bibleSummary?: BibleFinalSummary;
    }>("fleet.workPlan.resolveDecision", {
      agentId: state.agentsSelectedId,
      planId: params.planId,
      decisionId: params.decisionId,
      resolution: params.resolution,
    });
    state.workPlanDetail = res?.plan ?? state.workPlanDetail;
    state.workPlanOpenDecisions = Array.isArray(res?.openDecisions) ? res.openDecisions : [];
    state.workPlanBibleSummary = res?.bibleSummary ?? null;
    await loadFleetWorkPlans(state);
  } catch (err) {
    state.workPlansError = formatFleetWorkPlanError(err);
  } finally {
    state.workPlanBusy = false;
  }
}

export async function undoFleetWorkPlan(state: FleetWorkPlanState, planId: string) {
  if (!state.client || !state.connected || !state.agentsSelectedId) {
    return;
  }
  state.workPlanBusy = true;
  state.workPlansError = null;
  try {
    const res = await state.client.request<{ plan: FleetWorkPlanRecord }>("fleet.workPlan.undo", {
      agentId: state.agentsSelectedId,
      planId,
    });
    state.workPlanDetail = res?.plan ?? state.workPlanDetail;
    await loadFleetWorkPlanDetail(state, planId);
    await loadFleetWorkPlans(state);
  } catch (err) {
    state.workPlansError = formatFleetWorkPlanError(err);
  } finally {
    state.workPlanBusy = false;
  }
}

export function resetWorkPlanSelection(state: FleetWorkPlanState) {
  stopFleetWorkPlanPolling(state);
  state.workPlanSelectedId = null;
  state.workPlanDetail = null;
  state.workPlanOpenDecisions = [];
  state.workPlanBibleSummary = null;
  state.workPlanExpandedRoleIds = new Set();
  state.workPlanSessionLogs = {};
}

export function toggleFleetWorkPlanExpandedRole(state: FleetWorkPlanState, roleId: string) {
  const next = new Set(state.workPlanExpandedRoleIds);
  if (next.has(roleId)) {
    next.delete(roleId);
  } else {
    next.add(roleId);
  }
  state.workPlanExpandedRoleIds = next;
}

const TERMINAL_PLAN_STATUSES = new Set<FleetWorkPlanRecord["status"]>([
  "completed",
  "failed",
  "abandoned",
  "stopped",
]);
const TERMINAL_UNIT_STATUSES = new Set<FleetWorkPlanRecord["units"][number]["status"]>([
  "done",
  "failed",
  "cancelled",
]);

/**
 * Whether the selected plan should keep a quiet detail poll.
 *
 * Poll through approved/published plans that still have outstanding units
 * (including Planned, waiting on dispatch), and through `stopping` until
 * the registry settles. Do not poll drafts or terminal/stopped plans.
 */
export function planShouldPoll(plan: FleetWorkPlanRecord | null): boolean {
  if (!plan) {
    return false;
  }
  if (plan.status === "stopping") {
    return true;
  }
  if (plan.status === "draft" || TERMINAL_PLAN_STATUSES.has(plan.status)) {
    return false;
  }
  return plan.units.some((u) => !TERMINAL_UNIT_STATUSES.has(u.status));
}

/**
 * Start a quiet background poll for the selected plan. The poll reloads detail
 * while `planShouldPoll` is true, preserving expanded cards because their open
 * state lives on the host.
 */
export function startFleetWorkPlanPolling(
  state: FleetWorkPlanState,
  planId: string,
  intervalMs = 5000,
) {
  stopFleetWorkPlanPolling(state);
  if (!state.client || !state.connected || !state.agentsSelectedId) {
    return;
  }
  if (state.workPlanSelectedId === planId && !planShouldPoll(state.workPlanDetail)) {
    return;
  }
  state.workPlanPollInterval = window.setInterval(() => {
    if (
      !state.connected ||
      state.workPlanSelectedId !== planId ||
      !planShouldPoll(state.workPlanDetail)
    ) {
      stopFleetWorkPlanPolling(state);
      return;
    }
    void loadFleetWorkPlanDetail(state, planId, { quiet: true }).then(() => {
      void loadFleetWorkPlanSessionLogs(state);
    });
  }, intervalMs);
}

const SESSION_LOG_HISTORY_LIMIT = 40;

/**
 * Load operator-visible child-session transcripts for expanded agent cards.
 * Reuses `chat.history` so the dashboard does not need a second stream.
 */
export async function loadFleetWorkPlanSessionLogs(state: FleetWorkPlanState) {
  const client = state.client;
  if (!client || !state.connected) {
    return;
  }
  const planId = state.workPlanSelectedId;
  const keys = childSessionKeysToWatch(state.workPlanDetail, state.workPlanExpandedRoleIds);
  if (!planId || keys.length === 0) {
    return;
  }
  const next: Record<string, FleetSessionLogLine[]> = { ...state.workPlanSessionLogs };
  await Promise.all(
    keys.map(async (sessionKey) => {
      try {
        const res = await client.request<{ messages?: unknown[] }>("chat.history", {
          sessionKey,
          limit: SESSION_LOG_HISTORY_LIMIT,
        });
        if (state.workPlanSelectedId !== planId) {
          return;
        }
        next[sessionKey] = transcriptToLogLines(res?.messages);
      } catch {
        // Quiet: a missing child transcript must not clobber the dashboard.
      }
    }),
  );
  if (state.workPlanSelectedId !== planId) {
    return;
  }
  state.workPlanSessionLogs = next;
}

export function stopFleetWorkPlanPolling(state: FleetWorkPlanState) {
  if (state.workPlanPollInterval !== null) {
    window.clearInterval(state.workPlanPollInterval);
    state.workPlanPollInterval = null;
  }
}

/** Start or stop the work-plan poll based on whether the workplan panel is visible. */
export function syncFleetWorkPlanPolling(state: FleetWorkPlanState, onWorkPlanPanel: boolean) {
  if (!onWorkPlanPanel) {
    stopFleetWorkPlanPolling(state);
    return;
  }
  if (state.workPlanSelectedId) {
    startFleetWorkPlanPolling(state, state.workPlanSelectedId);
    void loadFleetWorkPlanDetail(state, state.workPlanSelectedId, { quiet: true });
  }
}

export type FleetSessionActivityView = {
  sessionKey: string;
  runningUnits: Array<{
    planId: string;
    unitId: string;
    title: string;
    status: string;
    childSessionKey?: string;
    runId?: string;
  }>;
  plannedUnits: Array<{
    planId: string;
    unitId: string;
    title: string;
    status: string;
    childSessionKey?: string;
    runId?: string;
  }>;
};

export async function loadFleetSessionActivity(
  state: {
    client: GatewayBrowserClient | null;
    connected: boolean;
    agentsSelectedId?: string | null;
  },
  params: { sessionKey: string; agentId?: string },
): Promise<FleetSessionActivityView | null> {
  const sessionKey = params.sessionKey.trim();
  const agentId = (params.agentId ?? state.agentsSelectedId ?? "").trim();
  if (!state.client || !state.connected || !sessionKey || !agentId) {
    return null;
  }
  try {
    const res = await state.client.request<FleetSessionActivityView>(
      "fleet.workPlan.sessionActivity",
      { sessionKey, agentId },
    );
    if (!res?.sessionKey) {
      return null;
    }
    return res;
  } catch {
    return null;
  }
}
